import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Submission {
  id: string;
  data_json: string;
  source_ip?: string;
  created_at: string;
}

interface FormDef {
  id: string;
  name: string;
  slug: string;
  published: number;
}

export default function FormAnalytics() {
  const [forms, setForms] = useState<FormDef[]>([]);
  const [selectedForm, setSelectedForm] = useState<string>('');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { loadForms(); }, []);
  useEffect(() => {
    if (selectedForm) loadSubmissions(selectedForm);
  }, [selectedForm]);

  async function loadForms() {
    try {
      setLoading(true);
      const res = await api.get('/api/forms');
      const list: FormDef[] = res.data ?? [];
      setForms(list);
      if (list.length > 0) setSelectedForm(list[0].id);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadSubmissions(formId: string) {
    try {
      const res = await api.get(`/api/forms/${formId}/submissions`);
      setSubmissions(res.data ?? []);
    } catch (err: any) {
      setError(err.message);
    }
  }

  const selected = forms.find((f) => f.id === selectedForm);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Form Analytics</h1>
      </div>
      {error && <div style={{ background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {forms.map((f) => (
          <button
            key={f.id}
            onClick={() => setSelectedForm(f.id)}
            style={{
              padding: '6px 14px',
              borderRadius: 20,
              border: '1px solid #e5e7eb',
              background: selectedForm === f.id ? '#111827' : '#fff',
              color: selectedForm === f.id ? '#fff' : '#525252',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {f.name}
          </button>
        ))}
      </div>

      {selected && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, flex: 1 }}>
            <div style={{ fontSize: 12, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Submissions</div>
            <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{submissions.length}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, flex: 1 }}>
            <div style={{ fontSize: 12, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
            <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{selected.published ? 'Published' : 'Draft'}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, flex: 1 }}>
            <div style={{ fontSize: 12, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Public URL</div>
            <div style={{ fontSize: 13, marginTop: 8, wordBreak: 'break-all' }}><a href={`/f/${selected.slug}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>/f/{selected.slug}</a></div>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={th}>Data</th>
                <th style={th}>IP</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((s) => (
                <tr key={s.id}>
                  <td style={td}>{new Date(s.created_at).toLocaleString()}</td>
                  <td style={td}>
                    <pre style={{ margin: 0, fontSize: 12, background: '#f8f8f6', padding: 8, borderRadius: 6, overflow: 'auto' }}>{JSON.stringify(JSON.parse(s.data_json ?? '{}'), null, 2)}</pre>
                  </td>
                  <td style={td}>{s.source_ip ?? '—'}</td>
                </tr>
              ))}
              {submissions.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 40 }}>
                    No submissions yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' };
const td: React.CSSProperties = { padding: '10px 14px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
